<script setup>
import {computed, defineProps, ref, onBeforeUnmount, watch} from "vue";
import {useNewsFeedStore} from "@/store/NewsFeed/newsFeed";
import {convertTimestampToData} from "@/tools/date-time-tools";
import { useNotificationsStore } from "@/store/notifications";
import {t} from "@/controllerLK/AvalLocalization";
import {highlightHtml} from "@/tools/highlight-html";
import CopyIcon from "@/components/Icons/CopyIcon.vue";
import TextClamp from "@/components/UI/TextClamp.vue";

const newsFeedStore = useNewsFeedStore();
const notificationsStore = useNotificationsStore();

const props = defineProps({
  isShowFullNews: {
    type: Boolean,
    default: false,
  },
});

// Сохранения ID новости в буфер при клике.
const saveIdToClipboard = async (id) => {
  try {
    await navigator.clipboard.writeText("id: " + id);
    notificationsStore.createNotify(t("map_page.toast.clipboard_success"), "success");
  } catch (e) {
    console.log(e);
    notificationsStore.createNotify(t("map_page.toast.clipboard_error"), "success");
  }
};

// Для youtube новостей извлекаем ролик.
const extractedIframe = computed(() => {
  const raw_text = newsFeedStore.currentNews.raw_text || "";
  const iframeRegex = /<iframe[^>]*>.*?<\/iframe>/gi;
  const iframeMatch = raw_text.match(iframeRegex);
  return iframeMatch ? iframeMatch[0] : "";
});

// Подсветка слов: query_highlights (из поисковой строки) — оранжевым,
// matched_terms каждой рубрики — своим цветом рубрики (из палитры).
const highlightOptions = computed(() => ({
  queryWords: newsFeedStore.currentNews?.query_highlights || [],
  rubrics: (newsFeedStore.currentNews?.rubrics || []).map((r) => ({
    name: r.rubric_name,
    terms: r.matched_terms || [],
  })),
}));
const highlightedRawText = computed(() => highlightHtml(
    newsFeedStore.currentNews?.raw_text,
    highlightOptions.value,
));
const highlightedPlainText = computed(() => highlightHtml(
    newsFeedStore.currentNews?.text,
    highlightOptions.value,
));

// ====== Лайтбокс изображений новости (свой fancybox).
const lightboxImages = ref([]);
const lightboxIndex = ref(0);
const isLightboxOpen = ref(false);

const openLightbox = (event) => {
  const target = event.target;
  if (!target || target.tagName !== "IMG") return;
  const container = event.currentTarget;
  const images = Array.from(container.querySelectorAll("img"))
    .map((img) => img.src)
    .filter((src) => !!src);
  if (!images.length) return;
  const clickedIndex = images.indexOf(target.src);
  lightboxImages.value = images;
  lightboxIndex.value = clickedIndex === -1 ? 0 : clickedIndex;
  isLightboxOpen.value = true;
};

const closeLightbox = () => {
  isLightboxOpen.value = false;
};

const showPrev = () => {
  if (!lightboxImages.value.length) return;
  const last = lightboxImages.value.length - 1;
  lightboxIndex.value = lightboxIndex.value === 0 ? last : lightboxIndex.value - 1;
};

const showNext = () => {
  if (!lightboxImages.value.length) return;
  const last = lightboxImages.value.length - 1;
  lightboxIndex.value = lightboxIndex.value === last ? 0 : lightboxIndex.value + 1;
};

const onKeydown = (e) => {
  if (!isLightboxOpen.value) return;
  if (e.key === "Escape") closeLightbox();
  else if (e.key === "ArrowLeft") showPrev();
  else if (e.key === "ArrowRight") showNext();
};

watch(isLightboxOpen, (open) => {
  if (open) {
    document.addEventListener("keydown", onKeydown);
    document.body.style.overflow = "hidden";
  } else {
    document.removeEventListener("keydown", onKeydown);
    document.body.style.overflow = "";
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});
</script>

<template>
  <div class="news-post-body">
    <!-- ====== ID и дата в полноэкранной новости располагается в заголовке  -->
    <div
      v-if="!props.isShowFullNews"
      class="news-post-body__meta"
    >
      <span class="news-post-body__meta-item news-post-body__meta-item-date">
        {{ convertTimestampToData(+newsFeedStore.currentNews.date) }}
      </span>
      <span
        v-tooltip:bottom="t('news_page_body_post.label.copy_id_tooltip')"
        class="news-post-body__meta-item news-post-body__meta-item-id"
        @click="saveIdToClipboard(newsFeedStore.currentNews.id)"
      >
        <span class="news-post-body__meta-value">
          {{ t('news_page_body_post.label.copy_id') }}
          <CopyIcon />
        </span>
      </span>
    </div>
    <!--  ID и дата в полноэкранной новости располагается в заголовке =========  -->

    <!-- ========  Для новостей, которые относятся к ютубу текст может сворачиваться.  -->
    <TextClamp
      v-if="newsFeedStore.currentNews.domain
        && newsFeedStore.currentNews.domain.includes('youtube')
        && !props.isShowFullNews"
      max-height="200"
    >
      <div
        v-dompurify-html="extractedIframe"
        class="news-post-body__content"
        @click="openLightbox"
      />
      <div
        v-dompurify-html="highlightedPlainText"
        class="news-post-body__content"
      />
    </TextClamp>
    <div
      v-else
      v-dompurify-html="highlightedRawText"
      class="news-post-body__content"
      @click="openLightbox"
    />
    <!-- ========  Для новостей, которые относятся к ютубу текст может сворачиваться.  -->

    <!-- ======== Лайтбокс изображений (fullscreen fancybox) -->
    <teleport to="body">
      <div
        v-if="isLightboxOpen"
        class="news-lightbox"
        :class="{ 'news-lightbox--with-thumbs': lightboxImages.length > 1 }"
        @click.self="closeLightbox"
      >
        <button
          class="news-lightbox__btn news-lightbox__btn--close"
          type="button"
          @click="closeLightbox"
        >
          ×
        </button>
        <button
          v-if="lightboxImages.length > 1"
          class="news-lightbox__btn news-lightbox__btn--prev"
          type="button"
          @click.stop="showPrev"
        >
          ‹
        </button>
        <img
          :src="lightboxImages[lightboxIndex]"
          class="news-lightbox__img"
          alt=""
        >
        <button
          v-if="lightboxImages.length > 1"
          class="news-lightbox__btn news-lightbox__btn--next"
          type="button"
          @click.stop="showNext"
        >
          ›
        </button>
        <div
          v-if="lightboxImages.length > 1"
          class="news-lightbox__counter"
        >
          {{ lightboxIndex + 1 }} / {{ lightboxImages.length }}
        </div>
        <div
          v-if="lightboxImages.length > 1"
          class="news-lightbox__thumbs"
          @click.stop
        >
          <button
            v-for="(src, i) in lightboxImages"
            :key="i"
            type="button"
            class="news-lightbox__thumb"
            :class="{ 'news-lightbox__thumb--active': i === lightboxIndex }"
            @click="lightboxIndex = i"
          >
            <img
              :src="src"
              alt=""
            >
          </button>
        </div>
      </div>
    </teleport>
    <!-- ======== Лайтбокс изображений (fullscreen fancybox) -->
  </div>
</template>

<style scoped lang="scss">

.news-post-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &__meta {
    display: flex;
    gap: 20px;
  }

  &__meta-item {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--text-text-pur);

    &-id {
      color: var(--text-text-pur);
    }
  }

  &__meta-value {
    color: var(--outline-grey3);
    cursor: default;
    margin-right: 15px;
    display: inline-flex;

    & svg {
      color: #1F9AFF;
      margin-left: 6px;
    }

    &:hover {
      cursor: default;

      & svg {
        color: #1A64D4;
      }
    }
  }

  &__content {
    display: block;
    word-break: break-word;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }
}

.news-lightbox {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  &--with-thumbs {
    padding-bottom: 140px;
  }

  &__img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
    user-select: none;
  }

  &__btn {
    position: absolute;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    padding: 0;
    line-height: 1;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }

    &--close {
      top: 20px;
      right: 20px;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      font-size: 32px;
    }

    &--prev,
    &--next {
      top: 50%;
      transform: translateY(-50%);
      width: 56px;
      height: 56px;
      border-radius: 50%;
      font-size: 44px;
    }

    &--prev {
      left: 20px;
    }

    &--next {
      right: 20px;
    }
  }

  &__counter {
    position: absolute;
    bottom: 130px;
    left: 50%;
    transform: translateX(-50%);
    color: #fff;
    font-size: 14px;
    background: rgba(0, 0, 0, 0.5);
    padding: 6px 14px;
    border-radius: 12px;
  }

  &__thumbs {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100% - 160px);
    display: flex;
    gap: 8px;
    padding: 10px;
    overflow-x: auto;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 10px;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.4) transparent;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.4);
      border-radius: 3px;
    }
  }

  &__thumb {
    flex: 0 0 auto;
    width: 80px;
    height: 80px;
    padding: 0;
    border: 2px solid transparent;
    border-radius: 6px;
    background: #000;
    cursor: pointer;
    overflow: hidden;
    transition: border-color 0.15s, opacity 0.15s;
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      pointer-events: none;
    }

    &--active {
      border-color: #1F9AFF;
      opacity: 1;
    }
  }
}
</style>


<!-- Стили для изображений, видео, iframe применяются глобально, без scoped.  -->
<style lang="scss">
.news-post-body__content {
  img {
    display: block !important;
    max-width: 100% !important;
    height: auto !important;
    border-radius: 8px;
    margin: 10px auto;
    cursor: zoom-in;
  }

  video {
    max-width: 100% !important;
    display: block !important;
    margin: 10px auto;
  }

  iframe {
    width: 100% !important;
    max-width: 100% !important;
  }
}

// Подсветка слов внутри новости: query_highlights и rubric_highlights.
// Сплошное подчёркивание, цвет задаётся inline (text-decoration-color)
// на каждом span'е — query оранжевый, каждая рубрика — своим цветом из палитры.
.news-hl {
  background: transparent;
  color: inherit;
  padding: 0;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  cursor: help;
}
</style>